import React from 'react'
import cn from 'classnames'
import { ReactSortable } from 'react-sortablejs'

import { TileSectionCategory, Tile, Order, Role } from './types'

import { Icons } from './Icons'
import { TileComponent } from './Tile'

import { useLocalStorage } from './useStorage'
import { filterRole, sortTilesBy } from './ToolboxTiles'

const smallDown = window.matchMedia('(min-width: 1024px)')

interface Props {
  reportRef: (ref: HTMLDivElement) => void
  category: TileSectionCategory
  id: string
  expanded: boolean
  uncollapsible?: boolean
  tiles: Tile[]
  tileSort: Order
  onTileSortChange: (order: Order) => void
  tileFilterRole?: Role
}

export const CategoryTiles: React.FC<Props> = ({
  reportRef,
  category,
  id,
  tiles,
  expanded,
  uncollapsible,
  tileSort,
  tileFilterRole,
  onTileSortChange,
}) => {
  const tilesSortableId = `${id}_tiles`
  let processedTiles = [...tiles]
  if (tileFilterRole) processedTiles = processedTiles.filter(filterRole(tileFilterRole))

  const [tilesSortData, setTilesSortData] = useLocalStorage(
    tilesSortableId,
    processedTiles.map((tile) => `tile-${tile.id}`).join('|')
  )

  processedTiles = processedTiles.sort(
    sortTilesBy(
      tileSort,
      tilesSortData.split('|').map((str) => parseInt(str.split('-')[1]))
    )
  )

  if (uncollapsible) console.log(tilesSortableId)

  return (
    <div
      ref={(elem) => elem && reportRef(elem)}
      className={cn('toolbox-tiles-category-container', { expanded })}
      {...(uncollapsible ? {} : { 'data-sortable-section': id })}>
      <div
        className={cn('toolbox-tiles-category-slab', 'flex align-items-center', { uncollapsible })}
        data-sortable-section-handle>
        {/* @ts-ignore */}
        <a name={category.slug} className="section-anchor toolbox-tiles-anchor" />
        <a href={'#' + category.slug} className="anchor-icon margin-right-l">
          <Icons.Link />
        </a>
        <div className="toolbox-tiles-category-slab-header flex-1-0-auto">
          <h3>{category.title}</h3>
        </div>
        {!uncollapsible && (
          <div className="toolbox-tiles-category-slab-body flex-none flex">
            <button
              type="button"
              className="button-square button-xsmall collapse-toggle"
              children={expanded ? '▼' : '▲'}
            />
          </div>
        )}
      </div>
      {expanded && (
        <>
          {tiles.length === 0 ? (
            <p style={{ margin: '0 0 1em 0.75rem' }}>No tiles in this category yet</p>
          ) : (
            processedTiles.length === 0 && <p style={{ margin: '0 0 1em 0.75rem' }}>No results with current filter</p>
          )}
        </>
      )}
      {tiles.length > 0 && (
        <ReactSortable
          className="toolbox-tiles"
          list={tilesSortData.split('|').map((tile) => ({ id: tile }))}
          setList={(newState) => setTilesSortData(newState.map((tile) => tile.id).join('|'))}
          group={tilesSortableId}
          draggable={'[data-sortable-tile]'}
          dataIdAttr={'data-sortable-tile'}
          animation={150}
          ghostClass={'dragging'}
          delay={smallDown.matches ? 0 : 100}
          onStart={(event) => {
            if (tileSort !== Order.USER) onTileSortChange(Order.USER)
          }}>
          {processedTiles.map((tile) => (
            <TileComponent key={tile.id} tile={tile} />
          ))}
        </ReactSortable>
      )}
    </div>
  )
}
